import { z } from 'zod';
import { eventBus, EventTypes } from '../services/EventBus';
import { eventStore } from '../services/EventStore';
import { workflowEngine } from '../services/WorkflowEngine';
import { unifiedSearchService } from '../services/UnifiedSearch';
import { metricsService } from '../services/MetricsService';
import { DataValidator } from '../services/DataValidation';
import { useAuth } from '../hooks/useAuth';

// Request/Response schemas
const EventQuerySchema = z.object({
  eventType: z.string().optional(),
  source: z.string().optional(),
  userId: z.string().optional(),
  tenantId: z.string().optional(),
  orgId: z.string().optional(),
  dateRange: z.object({
    start: z.string().datetime(),
    end: z.string().datetime()
  }).optional(),
  limit: z.number().min(1).max(1000).default(50),
  offset: z.number().min(0).default(0),
  orderBy: z.enum(['timestamp', 'sequenceNumber', 'storedAt']).default('timestamp'),
  orderDirection: z.enum(['asc', 'desc']).default('desc'),
  processed: z.boolean().optional()
});

const EventReplaySchema = z.object({
  eventIds: z.array(z.string()).min(1),
  replayId: z.string(),
  targetWorkflow: z.string().optional(),
  dryRun: z.boolean().default(false)
});

const WorkflowSchema = z.object({
  id: z.string(),
  name: z.string().min(1),
  description: z.string().optional(),
  type: z.enum(['project', 'support', 'cross-system']),
  status: z.enum(['active', 'paused', 'completed', 'cancelled']),
  steps: z.array(z.object({
    id: z.string(),
    name: z.string(),
    type: z.enum(['action', 'decision', 'wait', 'notification']),
    order: z.number(),
    conditions: z.record(z.any()).optional(),
    actions: z.array(z.object({
      type: z.string(),
      target: z.string(),
      parameters: z.record(z.any()).optional()
    }))
  })),
  triggers: z.array(z.object({
    eventType: z.string(),
    conditions: z.record(z.any()).optional()
  }))
});

const SearchQuerySchema = z.object({
  query: z.string().min(1),
  filters: z.object({
    type: z.array(z.string()).optional(),
    source: z.array(z.string()).optional(),
    dateRange: z.object({
      start: z.string().datetime(),
      end: z.string().datetime()
    }).optional(),
    status: z.array(z.string()).optional(),
    priority: z.array(z.string()).optional(),
    tags: z.array(z.string()).optional()
  }).optional(),
  sortBy: z.enum(['relevance', 'date', 'priority', 'status']).default('relevance'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
  limit: z.number().min(1).max(100).default(50),
  offset: z.number().min(0).default(0)
});

// API Response types
interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  timestamp: string;
}

interface PaginatedResponse<T> extends ApiResponse<T[]> {
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

// API Routes
export class ApiRoutes {
  // Events API
  static async getEvents(query: z.infer<typeof EventQuerySchema>): Promise<PaginatedResponse<any>> {
    try {
      const startTime = Date.now();
      
      // Validate query
      const validatedQuery = EventQuerySchema.parse(query);
      
      // Convert date strings to Date objects
      if (validatedQuery.dateRange) {
        validatedQuery.dateRange.start = new Date(validatedQuery.dateRange.start);
        validatedQuery.dateRange.end = new Date(validatedQuery.dateRange.end);
      }

      // Get events from store
      const events = await eventStore.queryEvents(validatedQuery);
      const stats = await eventStore.getEventStats();

      // Record API metrics
      const duration = Date.now() - startTime;
      metricsService.recordApiRequest('GET', '/api/events', 200, duration);

      return {
        success: true,
        data: events,
        pagination: {
          total: stats.totalEvents,
          limit: validatedQuery.limit,
          offset: validatedQuery.offset,
          hasMore: events.length === validatedQuery.limit
        },
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      const duration = Date.now() - (Date.now() - 100);
      metricsService.recordApiRequest('GET', '/api/events', 400, duration);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString()
      };
    }
  }

  static async replayEvents(request: z.infer<typeof EventReplaySchema>): Promise<ApiResponse<any>> {
    try {
      const startTime = Date.now();
      
      // Validate request
      const validatedRequest = EventReplaySchema.parse(request);

      // Replay events
      const result = await eventBus.replayEvents(
        validatedRequest.eventIds,
        validatedRequest.targetWorkflow
      );

      // Record API metrics
      const duration = Date.now() - startTime;
      metricsService.recordApiRequest('POST', '/api/events/replay', 200, duration);

      return {
        success: result.success,
        data: {
          replayedEvents: result.replayed,
          failedEvents: result.errors.length,
          errors: result.errors,
          replayId: validatedRequest.replayId
        },
        message: `Replayed ${result.replayed} events successfully`,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      const duration = Date.now() - (Date.now() - 100);
      metricsService.recordApiRequest('POST', '/api/events/replay', 400, duration);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString()
      };
    }
  }

  // Workflows API
  static async getWorkflows(): Promise<ApiResponse<any[]>> {
    try {
      const startTime = Date.now();
      
      const instances = workflowEngine.getAllInstances();
      const workflows = Object.values(workflowEngine.getWorkflows || {});

      const duration = Date.now() - startTime;
      metricsService.recordApiRequest('GET', '/api/workflows', 200, duration);

      return {
        success: true,
        data: {
          workflows,
          instances
        },
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      const duration = Date.now() - (Date.now() - 100);
      metricsService.recordApiRequest('GET', '/api/workflows', 400, duration);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString()
      };
    }
  }

  static async createWorkflow(workflow: z.infer<typeof WorkflowSchema>): Promise<ApiResponse<any>> {
    try {
      const startTime = Date.now();
      
      // Validate workflow
      const validatedWorkflow = WorkflowSchema.parse(workflow);
      
      // Validate with DataValidator
      DataValidator.validateWorkflow(validatedWorkflow);

      // Register workflow
      workflowEngine.registerWorkflow(validatedWorkflow);

      const duration = Date.now() - startTime;
      metricsService.recordApiRequest('POST', '/api/workflows', 201, duration);

      return {
        success: true,
        data: validatedWorkflow,
        message: 'Workflow created successfully',
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      const duration = Date.now() - (Date.now() - 100);
      metricsService.recordApiRequest('POST', '/api/workflows', 400, duration);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString()
      };
    }
  }

  static async updateWorkflow(id: string, workflow: Partial<z.infer<typeof WorkflowSchema>>): Promise<ApiResponse<any>> {
    try {
      const startTime = Date.now();
      
      // Validate workflow
      const validatedWorkflow = WorkflowSchema.partial().parse(workflow);
      
      // Update workflow (implementation would depend on storage)
      const updatedWorkflow = { id, ...validatedWorkflow };

      const duration = Date.now() - startTime;
      metricsService.recordApiRequest('PUT', `/api/workflows/${id}`, 200, duration);

      return {
        success: true,
        data: updatedWorkflow,
        message: 'Workflow updated successfully',
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      const duration = Date.now() - (Date.now() - 100);
      metricsService.recordApiRequest('PUT', `/api/workflows/${id}`, 400, duration);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString()
      };
    }
  }

  static async deleteWorkflow(id: string): Promise<ApiResponse<void>> {
    try {
      const startTime = Date.now();
      
      // Delete workflow (implementation would depend on storage)
      // workflowEngine.deleteWorkflow(id);

      const duration = Date.now() - startTime;
      metricsService.recordApiRequest('DELETE', `/api/workflows/${id}`, 200, duration);

      return {
        success: true,
        message: 'Workflow deleted successfully',
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      const duration = Date.now() - (Date.now() - 100);
      metricsService.recordApiRequest('DELETE', `/api/workflows/${id}`, 400, duration);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString()
      };
    }
  }

  // Search API
  static async search(query: z.infer<typeof SearchQuerySchema>): Promise<PaginatedResponse<any>> {
    try {
      const startTime = Date.now();
      
      // Validate query
      const validatedQuery = SearchQuerySchema.parse(query);
      
      // Convert date strings to Date objects
      if (validatedQuery.filters?.dateRange) {
        validatedQuery.filters.dateRange.start = new Date(validatedQuery.filters.dateRange.start);
        validatedQuery.filters.dateRange.end = new Date(validatedQuery.filters.dateRange.end);
      }

      // Perform search
      const results = await unifiedSearchService.search(validatedQuery);
      const stats = unifiedSearchService.getSearchStats();

      // Record search metrics
      const duration = Date.now() - startTime;
      metricsService.recordSearchQuery(validatedQuery.query, duration, results.length);

      const duration2 = Date.now() - startTime;
      metricsService.recordApiRequest('POST', '/api/search', 200, duration2);

      return {
        success: true,
        data: results,
        pagination: {
          total: Object.values(stats).reduce((sum: number, count: number) => sum + count, 0),
          limit: validatedQuery.limit,
          offset: validatedQuery.offset,
          hasMore: results.length === validatedQuery.limit
        },
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      const duration = Date.now() - (Date.now() - 100);
      metricsService.recordApiRequest('POST', '/api/search', 400, duration);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString()
      };
    }
  }

  // Metrics API
  static async getMetrics(): Promise<ApiResponse<string>> {
    try {
      const startTime = Date.now();
      
      const prometheusMetrics = metricsService.getPrometheusMetrics();

      const duration = Date.now() - startTime;
      metricsService.recordApiRequest('GET', '/api/metrics', 200, duration);

      return {
        success: true,
        data: prometheusMetrics,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      const duration = Date.now() - (Date.now() - 100);
      metricsService.recordApiRequest('GET', '/api/metrics', 400, duration);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString()
      };
    }
  }

  static async getMetricsSummary(): Promise<ApiResponse<any>> {
    try {
      const startTime = Date.now();
      
      const summary = metricsService.getMetricsSummary();

      const duration = Date.now() - startTime;
      metricsService.recordApiRequest('GET', '/api/metrics/summary', 200, duration);

      return {
        success: true,
        data: summary,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      const duration = Date.now() - (Date.now() - 100);
      metricsService.recordApiRequest('GET', '/api/metrics/summary', 400, duration);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString()
      };
    }
  }

  // Dead Letter Queue API
  static async getDeadLetterQueue(): Promise<ApiResponse<any[]>> {
    try {
      const startTime = Date.now();
      
      const dlq = eventBus.getDeadLetterQueue();

      const duration = Date.now() - startTime;
      metricsService.recordApiRequest('GET', '/api/dlq', 200, duration);

      return {
        success: true,
        data: dlq,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      const duration = Date.now() - (Date.now() - 100);
      metricsService.recordApiRequest('GET', '/api/dlq', 400, duration);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString()
      };
    }
  }

  static async retryDeadLetterEvent(dlqKey: string): Promise<ApiResponse<boolean>> {
    try {
      const startTime = Date.now();
      
      const success = await eventBus.retryDeadLetterEvent(dlqKey);

      const duration = Date.now() - startTime;
      metricsService.recordApiRequest('POST', `/api/dlq/retry/${dlqKey}`, success ? 200 : 400, duration);

      return {
        success,
        data: success,
        message: success ? 'Event retried successfully' : 'Failed to retry event',
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      const duration = Date.now() - (Date.now() - 100);
      metricsService.recordApiRequest('POST', `/api/dlq/retry/${dlqKey}`, 400, duration);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString()
      };
    }
  }

  static async clearDeadLetterQueue(): Promise<ApiResponse<number>> {
    try {
      const startTime = Date.now();
      
      const clearedCount = eventBus.clearDeadLetterQueue();

      const duration = Date.now() - startTime;
      metricsService.recordApiRequest('DELETE', '/api/dlq', 200, duration);

      return {
        success: true,
        data: clearedCount,
        message: `Cleared ${clearedCount} events from DLQ`,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      const duration = Date.now() - (Date.now() - 100);
      metricsService.recordApiRequest('DELETE', '/api/dlq', 400, duration);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString()
      };
    }
  }

  // Health Check API
  static async healthCheck(): Promise<ApiResponse<any>> {
    try {
      const startTime = Date.now();
      
      const health = {
        status: 'healthy',
        timestamp: new Date().toISOString(),
        services: {
          eventBus: eventBus.getConnectionStatus(),
          workflowEngine: {
            status: 'healthy',
            activeInstances: workflowEngine.getAllInstances().length
          },
          searchService: {
            status: 'healthy',
            indexSize: Object.values(unifiedSearchService.getSearchStats()).reduce((sum: number, count: number) => sum + count, 0)
          },
          metricsService: {
            status: 'healthy',
            metricsCount: Object.keys(metricsService.getMetricsSnapshot().metrics).length
          }
        }
      };

      const duration = Date.now() - startTime;
      metricsService.recordApiRequest('GET', '/api/health', 200, duration);

      return {
        success: true,
        data: health,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      const duration = Date.now() - startTime;
      metricsService.recordApiRequest('GET', '/api/health', 500, duration);
      
      return {
        success: false,
        data: {
          status: 'unhealthy',
          error: error instanceof Error ? error.message : 'Unknown error',
          timestamp: new Date().toISOString()
        },
        timestamp: new Date().toISOString()
      };
    }
  }

  // Stage 1 Launch Pad API Endpoints
  
  // List files for a project (client-safe)
  static async getProjectFiles(projectId: string): Promise<ApiResponse<any[]>> {
    try {
      const startTime = Date.now();
      
      // TODO: Replace with actual database query
      // For now, return mock data that matches the new interface
      const mockFiles = [
        {
          id: 'att-001',
          projectId,
          name: 'Tyler_ERP_Requirements.pdf',
          description: 'ERP system requirements document',
          size: 2048576,
          uploadedBy: { id: 'user-001', name: 'John Smith', email: 'john.smith@tylertech.com' },
          status: 'clean' as const,
          uploadedAt: '2024-08-24T10:30:00Z',
          sharepointUrl: 'https://tylertech.sharepoint.com/sites/projects/ERP_Implementation/Shared%20Documents/01_Project_Timeline/Tyler_ERP_Requirements.pdf'
        },
        {
          id: 'att-002',
          projectId,
          name: 'Data_Migration_Plan.xlsx',
          description: 'Data migration strategy and timeline',
          size: 1024000,
          uploadedBy: { id: 'user-002', name: 'Sarah Johnson', email: 'sarah.johnson@tylertech.com' },
          status: 'clean' as const,
          uploadedAt: '2024-08-23T14:15:00Z',
          sharepointUrl: 'https://tylertech.sharepoint.com/sites/projects/ERP_Implementation/Shared%20Documents/03_Conversion_Mapping/Data_Migration_Plan.xlsx'
        }
      ];

      const duration = Date.now() - startTime;
      metricsService.recordApiRequest('GET', `/api/onboarding/${projectId}/files`, 200, duration);

      return {
        success: true,
        data: mockFiles,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      const duration = Date.now() - startTime;
      metricsService.recordApiRequest('GET', `/api/onboarding/${projectId}/files`, 500, duration);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString()
      };
    }
  }

  // Notification acknowledgement
  static async acknowledgeNotification(notificationId: string): Promise<ApiResponse<boolean>> {
    try {
      const startTime = Date.now();
      
      // Try to call the actual backend first
      const response = await fetch('/api/notifications/ack', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notificationId })
      });
      
      if (response.ok) {
        const data = await response.json();
        const duration = Date.now() - startTime;
        metricsService.recordApiRequest('POST', '/api/notifications/ack', 200, duration);
        return data;
      }
      
      // Fallback to mock data if backend is unavailable and demo mode is enabled
      const { isDemoMode } = await import('../config/demo');
      if (isDemoMode()) {
        console.log('[API] Backend unavailable, using mock data for acknowledgeNotification');
        const { mockApiServer } = await import('./mockServer');
        const mockResponse = mockApiServer.acknowledgeNotification(notificationId);
        
        const duration = Date.now() - startTime;
        metricsService.recordApiRequest('POST', '/api/notifications/ack', 200, duration, { mock: true });
        
        return mockResponse;
      } else {
        throw new Error('Backend unavailable and demo mode is disabled');
      }
    } catch (error) {
      const startTime = Date.now();
      
      // Fallback to mock data on error if demo mode is enabled
      const { isDemoMode } = await import('../config/demo');
      if (isDemoMode()) {
        try {
          const { mockApiServer } = await import('./mockServer');
          const mockResponse = mockApiServer.acknowledgeNotification(notificationId);
          
          const duration = Date.now() - startTime;
          metricsService.recordApiRequest('POST', '/api/notifications/ack', 200, duration, { mock: true, error: true });
          
          return mockResponse;
        } catch (mockError) {
          console.error('Mock fallback also failed:', mockError);
        }
      }
      
      // If demo mode is disabled or mock fallback failed, return error
      const duration = Date.now() - startTime;
      metricsService.recordApiRequest('POST', '/api/notifications/ack', 500, duration);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString()
      };
    }
  }

  // Latest billing snapshot (read-only)
  static async getBillingSnapshot(projectId: string): Promise<ApiResponse<any>> {
    try {
      const startTime = Date.now();
      
      // TODO: Replace with actual database query
      // For now, return mock data
      const mockSnapshot = {
        status: 'published',
        cycleDate: '2024-08-01',
        lastActual: {
          period: '2024-07-01 to 2024-07-31',
          total: 125000,
          breakdown: [
            { item: 'Implementation Services', amount: 80000, type: 'hourly' },
            { item: 'Software Licenses', amount: 45000, type: 'saas' }
          ]
        },
        current: {
          period: '2024-08-01 to 2024-08-31',
          total: 98000,
          breakdown: [
            { item: 'Implementation Services', amount: 65000, type: 'hourly' },
            { item: 'Change Order - SSO Integration', amount: 33000, type: 'milestone', isChangeOrder: true }
          ]
        },
        next: {
          period: '2024-09-01 to 2024-09-30',
          total: 75000,
          breakdown: [
            { item: 'Implementation Services', amount: 50000, type: 'hourly' },
            { item: 'Training & Go-Live Support', amount: 25000, type: 'milestone' }
          ]
        },
        invoices: [
          {
            id: 'inv-001',
            number: 'INV-2024-001',
            amount: 125000,
            sentDate: '2024-08-05T10:00:00Z',
            sharepointUrl: 'https://tylertech.sharepoint.com/sites/projects/ERP_Implementation/Shared%20Documents/99_Invoices/INV-2024-001.pdf'
          }
        ]
      };

      const duration = Date.now() - startTime;
      metricsService.recordApiRequest('GET', `/api/billing/${projectId}/snapshot`, 200, duration);

      return {
        success: true,
        data: mockSnapshot,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      const duration = Date.now() - startTime;
      metricsService.recordApiRequest('GET', `/api/billing/${projectId}/snapshot`, 500, duration);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString()
      };
    }
  }
}

// Export types
export type { ApiResponse, PaginatedResponse };